export const MITUTOYO_BRIDGE_VERSION = '1';
export const MITUTOYO_HEIGHT_MESSAGE = 'mitutoyo-bridge/height';
export const MITUTOYO_HEIGHT_RECEIPT = 'mitutoyo-bridge/height-receipt';
export const MITUTOYO_SSO_ORIGIN = 'https://sso.mitutoyo.com.cn';
export const MITUTOYO_TEST_SSO_ORIGIN = 'https://sso.softinsky.com';

const TRUSTED_SSO_ORIGINS = new Set([
  MITUTOYO_SSO_ORIGIN,
  MITUTOYO_TEST_SSO_ORIGIN,
]);

const TRUSTED_PARENT_ORIGINS = new Set([
  'https://www.mitutoyo.com.cn',
  'https://lite.ivymobi.com.cn',
  'http://sfsite.akchina.com',
]);

// Appointment has no Lite relay. Keep this purpose policy narrower than height/SSO.
const APPOINTMENT_PARENT_BY_CHILD: Readonly<Record<string, string>> = Object.freeze({
  'https://mitutoyo.yangben.cn': 'https://www.mitutoyo.com.cn',
  'https://mitutoyo.ivymobi.com.cn': 'http://sfsite.akchina.com',
});

export function resolveAppointmentParentOrigin(referrer: string, childOrigin: string): string | null {
  if (!referrer) return null;
  try {
    const parent = new URL(referrer);
    if (parent.username || parent.password) return null;
    return APPOINTMENT_PARENT_BY_CHILD[childOrigin] === parent.origin ? parent.origin : null;
  } catch (_error) {
    return null;
  }
}

export type TrustedSsoResult =
  | { status: 'authenticated'; ticket: string }
  | { status: 'anonymous' };

export type TrustedSsoHandlerOptions = {
  getExpectedSource: () => Window | null;
  expectedOrigin?: string;
  onTicket: (ticket: string) => void | Promise<void>;
  onAnonymous?: () => void;
};

export function parseTrustedSsoMessage(
  event: MessageEvent,
  expectedSource: Window | null,
  expectedOrigin = MITUTOYO_SSO_ORIGIN,
): TrustedSsoResult | null {
  if (
    expectedSource === null
    || !TRUSTED_SSO_ORIGINS.has(expectedOrigin)
    || event.origin !== expectedOrigin
    || event.source !== expectedSource
    || event.data === null
    || typeof event.data !== 'object'
  ) {
    return null;
  }
  const data = event.data as Record<string, unknown>;
  if (data.type !== 'sso-check-result' || typeof data.isLogin !== 'boolean') {
    return null;
  }
  if (!data.isLogin) {
    return { status: 'anonymous' };
  }
  if (typeof data.ticket !== 'string' || data.ticket.trim().length === 0) {
    return null;
  }
  return { status: 'authenticated', ticket: data.ticket };
}

export function createTrustedSsoMessageHandler(
  options: TrustedSsoHandlerOptions,
): (event: MessageEvent) => Promise<void> {
  return async (event: MessageEvent) => {
    const result = parseTrustedSsoMessage(
      event,
      options.getExpectedSource(),
      options.expectedOrigin,
    );
    if (result?.status === 'authenticated') {
      await options.onTicket(result.ticket);
      return;
    }
    if (result?.status === 'anonymous') {
      options.onAnonymous?.();
    }
  };
}

export function resolveTrustedParentOrigin(
  referrer: string,
  rootUrl?: string | null,
): string | null {
  const candidate = referrer || rootUrl;
  if (!candidate) {
    return null;
  }
  try {
    const origin = new URL(candidate).origin;
    return TRUSTED_PARENT_ORIGINS.has(origin) ? origin : null;
  } catch (_error) {
    return null;
  }
}

export function createHeightMessage(
  requestId: string,
  height: number,
  width: number,
): Record<string, unknown> {
  return {
    type: MITUTOYO_HEIGHT_MESSAGE,
    version: MITUTOYO_BRIDGE_VERSION,
    requestId,
    height,
    width,
  };
}

export function isTrustedHeightReceipt(
  event: MessageEvent,
  expectedSource: Window,
  expectedOrigin: string,
  expectedRequestId: string,
): boolean {
  if (
    event.source !== expectedSource
    || event.origin !== expectedOrigin
    || event.data === null
    || typeof event.data !== 'object'
  ) {
    return false;
  }
  const data = event.data as Record<string, unknown>;
  return data.type === MITUTOYO_HEIGHT_RECEIPT
    && data.version === MITUTOYO_BRIDGE_VERSION
    && data.requestId === expectedRequestId
    && typeof data.height === 'number'
    && Number.isFinite(data.height)
    && data.height >= 0;
}

export type MitutoyoHeightBridge = {
  enabled: boolean;
  notify: () => void;
  cleanup: () => void;
};

export type MitutoyoHeightBridgeOptions = {
  window: Window;
  document: Document;
  rootUrl?: string | null;
  resizeObserver?: typeof ResizeObserver;
  mutationObserver?: typeof MutationObserver;
  timeoutMs?: number;
  createRequestId?: () => string;
  onReceiptTimeout?: (requestId: string) => void;
};

function measureDocument(documentRef: Document, windowRef: Window): { height: number; width: number } {
  const html = documentRef.documentElement;
  const body = documentRef.body;
  const portalBottom = typeof documentRef.querySelectorAll === 'function'
    ? Array.from(documentRef.querySelectorAll(
      '[role="dialog"], .MuiModal-root, .MuiPopover-root',
    )).reduce((bottom, element) => {
      const bounds = element.getBoundingClientRect();
      return Math.max(bottom, bounds.bottom + (windowRef.scrollY ?? 0));
    }, 0)
    : 0;
  return {
    height: Math.max(
      html?.scrollHeight ?? 0,
      html?.offsetHeight ?? 0,
      html?.clientHeight ?? 0,
      body?.scrollHeight ?? 0,
      body?.offsetHeight ?? 0,
      body?.clientHeight ?? 0,
      portalBottom,
    ),
    width: Math.max(
      html?.clientWidth ?? 0,
      body?.scrollWidth ?? 0,
      windowRef.innerWidth ?? 0,
    ),
  };
}

function createLegacyHeightMessage(height: number, width: number): Record<string, unknown> {
  return {
    message: 'SFCONTENT_INFO_MESSAGE',
    data: {
      heigth: height,
      height,
      width,
    },
  };
}

export function startMitutoyoHeightBridge(
  options: MitutoyoHeightBridgeOptions,
): MitutoyoHeightBridge {
  const windowRef = options.window;
  const documentRef = options.document;
  const parentOrigin = resolveTrustedParentOrigin(documentRef.referrer, options.rootUrl);
  const ResizeObserverCtor = options.resizeObserver
    ?? (windowRef as Window & { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
  const MutationObserverCtor = options.mutationObserver
    ?? (windowRef as Window & { MutationObserver?: typeof MutationObserver }).MutationObserver;
  if (
    windowRef.parent === windowRef.self
    || parentOrigin === null
    || typeof ResizeObserverCtor !== 'function'
  ) {
    return {
      enabled: false,
      notify: () => undefined,
      cleanup: () => undefined,
    };
  }

  const timeoutMs = options.timeoutMs ?? 2000;
  const createRequestId = options.createRequestId ?? (() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `height-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  });
  let active = true;
  let frameScheduled = false;
  let frameId: number | null = null;
  let lastSize: { height: number; width: number } | null = null;
  let pending: { requestId: string; timer: ReturnType<typeof setTimeout> } | null = null;

  const clearPending = () => {
    if (pending !== null) {
      clearTimeout(pending.timer);
      pending = null;
    }
  };
  const sendHeight = () => {
    if (!active) {
      return;
    }
    const size = measureDocument(documentRef, windowRef);
    if (lastSize?.height === size.height && lastSize?.width === size.width) {
      return;
    }
    lastSize = size;
    clearPending();
    const requestId = createRequestId();
    windowRef.parent.postMessage(
      createHeightMessage(requestId, size.height, size.width),
      parentOrigin,
    );
    windowRef.parent.postMessage(
      createLegacyHeightMessage(size.height, size.width),
      parentOrigin,
    );
    const timer = setTimeout(() => {
      if (pending?.requestId !== requestId) {
        return;
      }
      pending = null;
      options.onReceiptTimeout?.(requestId);
    }, timeoutMs);
    pending = { requestId, timer };
  };
  const notify = () => {
    if (!active || frameScheduled) {
      return;
    }
    frameScheduled = true;
    const scheduledFrame = windowRef.requestAnimationFrame(() => {
      frameScheduled = false;
      frameId = null;
      sendHeight();
    });
    frameId = frameScheduled ? scheduledFrame : null;
  };
  const handleReceipt = (event: Event) => {
    if (
      pending !== null
      && isTrustedHeightReceipt(
        event as MessageEvent,
        windowRef.parent,
        parentOrigin,
        pending.requestId,
      )
    ) {
      clearPending();
    }
  };
  const observer = new ResizeObserverCtor(notify);
  const mutationObserver = typeof MutationObserverCtor === 'function'
    ? new MutationObserverCtor(notify)
    : null;
  if (documentRef.documentElement) {
    observer.observe(documentRef.documentElement);
  }
  if (documentRef.body) {
    observer.observe(documentRef.body);
    mutationObserver?.observe(documentRef.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  }
  windowRef.addEventListener('message', handleReceipt);
  windowRef.addEventListener('resize', notify);
  notify();

  return {
    enabled: true,
    notify,
    cleanup: () => {
      if (!active) {
        return;
      }
      active = false;
      observer.disconnect();
      mutationObserver?.disconnect();
      if (frameId !== null) {
        windowRef.cancelAnimationFrame(frameId);
        frameId = null;
      }
      clearPending();
      windowRef.removeEventListener('message', handleReceipt);
      windowRef.removeEventListener('resize', notify);
    },
  };
}
